/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

var CopyLinkButton = class extends HTMLElement {
  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    clearTimeout(this._confirmationTimeout);
  }

  _onClick(event) {
    event.preventDefault();

    var url = this.getAttribute('url');
    if (!url) return;

    this._copyUrl(url).then(function (success) {
      if (success) this._showConfirmation();
    }.bind(this));
  }

  _copyUrl(url) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(url).then(function () {
        return true;
      }).catch(function () {
        return false;
      });
    }

    var textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    var success = false;

    try {
      success = document.execCommand('copy');
    } catch (error) {
      success = false;
    }

    document.body.removeChild(textarea);
    return Promise.resolve(success);
  }

  _showConfirmation() {
    var trigger = this.querySelector('a[role="button"]');

    if (trigger) {
      if (!this._defaultLabel) {
        this._defaultLabel = trigger.getAttribute('aria-label');
      }

      trigger.setAttribute('aria-label', 'Copied!');
    }

    this.classList.add('is-copied');

    clearTimeout(this._confirmationTimeout);
    this._confirmationTimeout = setTimeout(function () {
      this.classList.remove('is-copied');

      if (trigger && this._defaultLabel) {
        trigger.setAttribute('aria-label', this._defaultLabel);
      }
    }.bind(this), 2000);
  }
};

if (!window.customElements.get('copy-link-button')) {
  window.customElements.define('copy-link-button', CopyLinkButton);
}

document.addEventListener('alpine:init', () => {
  Alpine.data('CustomFormLoader', () => ({
    loading: !!new URLSearchParams(window.location.search).get('product'),
    hasProductParam: !!new URLSearchParams(window.location.search).get('product'),
    hasFromCartParam: new URLSearchParams(window.location.search).has('from_cart'),

    syncCartViewState() {
      const section = this.$el;
      if (!section || !section.classList.contains('ak-wholesale-order')) return;

      const params = new URLSearchParams(window.location.search);
      const fromCart = params.has('from_cart');
      const submitted =
        section.getAttribute('data-ak-order-submitted') === '1' ||
        params.get('wholesale_submitted') === '1' ||
        params.get('contact_posted') === '1';
      const count = window.AkWholesaleCart?.getCount() || 0;

      if (params.has('product')) {
        section.setAttribute('data-ak-view', 'picker');
        return;
      }

      if (submitted) {
        section.setAttribute('data-ak-view', 'checkout');
        return;
      }

      section.setAttribute('data-ak-view', fromCart && count > 0 ? 'checkout' : 'picker');
    },

    async init() {
      this.sectionId = this.$el.dataset.sectionId || '';
      this.syncCartViewState();
      document.addEventListener('ak-wholesale-cart:updated', () => this.syncCartViewState());

      // get product handle from url
      const urlParams = new URLSearchParams(window.location.search);
      const productHandle = urlParams.get('product');
      if (!productHandle) return;

      try {
        const fetchParams = new URLSearchParams(window.location.search);
        fetchParams.set('section_id', this.sectionId);
        fetchParams.delete('product');
        const response = await fetch(`/products/${productHandle}?${fetchParams.toString()}`);
        const data = await response.text();
        let $fetchedSection = new DOMParser().parseFromString(data, 'text/html').querySelector('.shopify-section');

        if (!$fetchedSection) {
          this.loading = false;
          this.syncCartViewState();
          return;
        }

        this.$el.replaceWith($fetchedSection);

        if (window.Alpine) {
          Alpine.initTree($fetchedSection);
        }

        if (typeof window.bindAllWholesalePdfForms === 'function') {
          window.bindAllWholesalePdfForms();
        }

        const akSection =
          $fetchedSection.querySelector('section.ak-wholesale-order') ||
          document.querySelector('section.ak-wholesale-order[data-section-id="' + this.sectionId + '"]');

        if (akSection) {
          const params = new URLSearchParams(window.location.search);
          const fromCart = params.has('from_cart');
          const submitted =
            akSection.getAttribute('data-ak-order-submitted') === '1' ||
            params.get('wholesale_submitted') === '1' ||
            params.get('contact_posted') === '1';
          const count = window.AkWholesaleCart?.getCount() || 0;

          if (params.has('product')) {
            akSection.setAttribute('data-ak-view', 'picker');
          } else if (submitted) {
            akSection.setAttribute('data-ak-view', 'checkout');
          } else {
            akSection.setAttribute('data-ak-view', fromCart && count > 0 ? 'checkout' : 'picker');
          }
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.syncCartViewState();
      }
    }
  }));

  Alpine.data('WholesaleOrderForm', () => ({
    sectionId: '',
    sku: '',
    color: '',
    productFormId: '',
    hasOnlyDefaultVariant: false,
    minimumOrderQuantity: 0,
    moqErrorTemplate: '',
    qtyErrorType: 'none',
    qtyErrorMessage: '',
    quantities: {},
    sizeOptions: [],
    media: [],
    variants: [],
    colorOptionPosition: null,
    selectedMediaId: null,
    selectedColorValue: '',
    wholesaleTiers: [],
    styleNo: '',
    initialPrice: '',
    priceListReady: false,
    customOrderFields: false,
    customizationTypes: [],
    customizationOther: '',
    designDescription: '',
    logoFile: null,
    designRefFile: null,
    logoFileUrl: '',
    designRefFileUrl: '',
    logoUploading: false,
    designRefUploading: false,
    fieldErrors: {},
    customErrorMessage: '',
    showFormSuccess: false,
    customizationRequiredError: '',
    otherRequiredError: '',
    fileTypeErrorTemplate: '',
    fileSizeErrorTemplate: '',
    allowedFileTypes: [],
    maxFileSizeMb: 10,
    multiSkuOrder: false,

    init() {
      this.sectionId = this.$el.dataset.sectionId || '';
      this.productFormId = this.$el.dataset.productFormId || '';
      this.sku = this.$el.dataset.initialSku || '';
      this.color = this.$el.dataset.initialColor || '';

      const variantScript = this.$el.querySelector('script[data-variant]');
      if (variantScript) {
        try {
          const variant = JSON.parse(variantScript.textContent);
          if (variant?.sku) this.sku = variant.sku;
        } catch (error) {
          // ignore parse errors
        }
      }
      this.hasOnlyDefaultVariant = this.$el.dataset.hasOnlyDefaultVariant === 'true';
      this.minimumOrderQuantity = parseInt(this.$el.dataset.minimumOrderQuantity, 10) || 0;
      this.moqErrorTemplate = this.$el.dataset.moqErrorMessage || '';
      this.colorOptionPosition = parseInt(this.$el.dataset.colorOptionPosition, 10) || null;
      this.selectedMediaId = parseInt(this.$el.dataset.initialMediaId, 10) || null;
      this.selectedColorValue = (this.$el.dataset.initialColor || '').toLowerCase();
      this.initialPrice = this.$el.dataset.initialPrice || '';
      this.styleNo = window.AkPriceList?.extractStyleNo(this.$el.dataset.productTitle || '') || '';
      if (!this.styleNo && this.sku) {
        this.styleNo = window.AkPriceList?.extractStyleNo(this.sku) || '';
      }
      this.customOrderFields = this.$el.dataset.customOrderFields === 'true';
      this.multiSkuOrder = this.$el.dataset.multiSkuOrder === 'true';

      if (this.customOrderFields) {
        this.fileTypeErrorTemplate = this.$el.dataset.fileTypeError || '';
        this.fileSizeErrorTemplate = this.$el.dataset.fileSizeError || '';
        this.maxFileSizeMb = parseInt(this.$el.dataset.maxFileSizeMb, 10) || 10;
        this.allowedFileTypes = (this.$el.dataset.allowedFileTypes || '')
          .split(',')
          .map((type) => type.trim().toLowerCase())
          .filter(Boolean);

        const customizationFieldset = this.$el.querySelector('.ak-wholesale-order__customization');
        if (customizationFieldset) {
          this.customizationRequiredError = customizationFieldset.dataset.customizationRequiredError || '';
          this.otherRequiredError = customizationFieldset.dataset.otherRequiredError || '';
        }
      }

      try {
        this.sizeOptions = JSON.parse(this.$el.dataset.sizeOptions || '[]');
      } catch (error) {
        this.sizeOptions = [];
      }

      const mediaScript = this.$el.querySelector('[data-wholesale-media]');
      if (mediaScript) {
        try {
          const data = JSON.parse(mediaScript.textContent);
          this.media = data.media || [];
          this.variants = data.variants || [];
        } catch (error) {
          this.media = [];
          this.variants = [];
        }
      }

      this.syncQuantities();
      this.initWholesalePrice();
      this.bindContactFormSubmit();
      this.bindNumericInputs();
      this.syncReturnTo();
      this.syncContactHiddenFields();
      this.checkFormSuccessParam();

      document.addEventListener('variant:changed', (event) => {
        if (!this.$el.isConnected) return;
        if (this.productFormId && event.target?.getAttribute('id') !== this.productFormId) return;

        const variant = event.detail.variant;
        if (!variant) return;

        this.sku = variant.sku || '';
        this.color = this.getSelectedColor();
        this.updateMedia(variant);
        this.updateSelectedColorDisplay();
        this.syncContactHiddenFields();
        this.updateWholesalePrice();
      });
    },

    bindNumericInputs() {
      this.$el.querySelectorAll('.ak-wholesale-order__qty--numeric').forEach((input) => {
        if (input.dataset.numericBound === 'true') return;
        input.dataset.numericBound = 'true';
        input.addEventListener('input', (event) => this.sanitizeNumeric(event));
        input.addEventListener('keydown', (event) => {
          if (['e', 'E', '+', '-', '.'].includes(event.key)) event.preventDefault();
        });
      });
    },

    sanitizeNumeric(event) {
      const input = event.target;
      const sanitized = (input.value || '').replace(/\D/g, '');
      if (input.value !== sanitized) input.value = sanitized;
    },

    validateFile(file, fieldKey) {
      if (!file) {
        this.fieldErrors[fieldKey] = '';
        return true;
      }

      const extension = file.name.split('.').pop()?.toLowerCase() || '';

      if (this.allowedFileTypes.length && !this.allowedFileTypes.includes(extension)) {
        this.fieldErrors[fieldKey] = this.fileTypeErrorTemplate.replace('{types}', this.allowedFileTypes.join(', '));
        return false;
      }

      const maxBytes = this.maxFileSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        this.fieldErrors[fieldKey] = this.fileSizeErrorTemplate.replace('{size}', this.maxFileSizeMb);
        return false;
      }

      this.fieldErrors[fieldKey] = '';
      return true;
    },

    async handleFileChange(event, fieldKey) {
      const file = event.target.files?.[0] || null;

      if (fieldKey === 'logo') {
        this.logoFile = file;
        this.logoFileUrl = '';

        if (!this.validateFile(file, 'logo')) {
          event.target.value = '';
          this.logoFile = null;
        } else if (file) {
          this.logoUploading = true;
          const uploadingFile = file;

          try {
            const result = await cloudinaryUpload(uploadingFile);
            if (this.logoFile === uploadingFile) {
              this.logoFileUrl = result.secureUrl;
              this.fieldErrors.logo = '';
            }
          } catch (error) {
            if (this.logoFile === uploadingFile) {
              this.fieldErrors.logo = error?.message || 'Upload failed. Please try again.';
              this.logoFile = null;
              event.target.value = '';
            }
          } finally {
            if (this.logoFile === uploadingFile || !this.logoFile) {
              this.logoUploading = false;
            }
          }
        }
      } else if (fieldKey === 'designRef') {
        this.designRefFile = file;
        this.designRefFileUrl = '';

        if (!this.validateFile(file, 'designRef')) {
          event.target.value = '';
          this.designRefFile = null;
        } else if (file) {
          this.designRefUploading = true;
          const uploadingFile = file;

          try {
            const result = await cloudinaryUpload(uploadingFile);
            if (this.designRefFile === uploadingFile) {
              this.designRefFileUrl = result.secureUrl;
              this.fieldErrors.designRef = '';
            }
          } catch (error) {
            if (this.designRefFile === uploadingFile) {
              this.fieldErrors.designRef = error?.message || 'Upload failed. Please try again.';
              this.designRefFile = null;
              event.target.value = '';
            }
          } finally {
            if (this.designRefFile === uploadingFile || !this.designRefFile) {
              this.designRefUploading = false;
            }
          }
        }
      }

      this.syncCustomOrderHiddenFields();
    },

    get hasOtherCustomization() {
      return this.customizationTypes.some((type) => type.toLowerCase().includes('other'));
    },

    syncContactHiddenFields() {
      const form = this.$el.querySelector('form.ak-wholesale-order__contact-form');
      if (!form) return;

      const skuInput = form.querySelector('[name="contact[SKU]"]');
      const colorInput = form.querySelector('[name="contact[Selected Color]"]');

      if (skuInput) skuInput.value = this.sku || '';
      if (colorInput) colorInput.value = this.color || '';
    },

    syncCustomOrderHiddenFields() {
      if (this.$refs.customizationTypesHidden) {
        this.$refs.customizationTypesHidden.value = this.customizationTypes.join(', ');
      }

      if (this.$refs.customizationOtherHidden) {
        this.$refs.customizationOtherHidden.value = (this.customizationOther || '').trim();
      }

      if (this.$refs.designDescriptionHidden) {
        this.$refs.designDescriptionHidden.value = (this.designDescription || '').trim();
      }

      if (this.$refs.logoFileNameHidden) {
        this.$refs.logoFileNameHidden.value = this.logoFile ? this.logoFile.name : '';
      }

      if (this.$refs.logoFileUrlHidden) {
        this.$refs.logoFileUrlHidden.value = this.logoFileUrl || '';
      }

      if (this.$refs.designRefFileNameHidden) {
        this.$refs.designRefFileNameHidden.value = this.designRefFile ? this.designRefFile.name : '';
      }

      if (this.$refs.designRefUrlHidden) {
        this.$refs.designRefUrlHidden.value = this.designRefFileUrl || '';
      }
    },

    populateCustomOrderFields() {
      this.syncContactHiddenFields();
      this.syncCustomOrderHiddenFields();
    },

    validateCustomOrderFields() {
      this.fieldErrors = {};
      this.customErrorMessage = '';
      this.qtyErrorType = 'none';
      this.qtyErrorMessage = '';
      let valid = true;

      if (this.$el.querySelector('.ak-wholesale-order__customization')) {
        if (!this.customizationTypes.length) {
          this.fieldErrors.customization = this.customizationRequiredError;
          valid = false;
        }

        if (this.hasOtherCustomization && !(this.customizationOther || '').trim()) {
          this.fieldErrors.other = this.otherRequiredError;
          valid = false;
        }
      }

      if (this.logoFile && this.$el.querySelector('[data-custom-file="logo"]') && !this.validateFile(this.logoFile, 'logo')) valid = false;
      if (this.designRefFile && this.$el.querySelector('[data-custom-file="design-ref"]') && !this.validateFile(this.designRefFile, 'designRef')) valid = false;

      return valid;
    },

    showCustomError() {
      this.$nextTick(() => {
        const errorEl = this.$el.querySelector('.ak-wholesale-order__customization') || this.$refs.qtyError;
        if (!errorEl) return;

        errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        if (typeof errorEl.focus === 'function') errorEl.focus({ preventScroll: true });
      });
    },

    checkFormSuccessParam() {
      const params = new URLSearchParams(window.location.search);

      this.showFormSuccess = params.get('wholesale_submitted') === '1' || params.get('contact_posted') === '1';
      this.cleanSuccessParams();

      this.$nextTick(() => {
        const successEl = this.$refs.formSuccess;
        if (!successEl) return;

        successEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },

    cleanSuccessParams() {
      const url = new URL(window.location.href);
      url.searchParams.delete('wholesale_submitted');
      url.searchParams.delete('contact_posted');
      const search = url.searchParams.toString();
      const nextUrl = search ? `${url.pathname}?${search}` : url.pathname;
      window.history.replaceState({}, '', nextUrl + url.hash);
    },

    async initWholesalePrice() {
      if (!window.AkPriceList) return;

      await (window.AkPriceList.ready || Promise.resolve());
      this.priceListReady = window.AkPriceList.isReady();
      this.updateWholesalePrice();
    },

    updateWholesalePrice() {
      if (!window.AkPriceList || !this.priceListReady) {
        this.wholesaleTiers = [];
        return;
      }

      const title = this.$el.dataset.productTitle || '';
      let row = window.AkPriceList.findPriceForSource(title);
      if (row?.tiers) {
        this.styleNo = row.styleNo;
      } else if (this.sku) {
        row = window.AkPriceList.findPriceForSource(this.sku);
        if (row?.tiers) this.styleNo = row.styleNo;
      }

      if (!row?.tiers) {
        this.wholesaleTiers = [];
        window.debugLog('[AkPriceList] product lookup', {
          title: title,
          sku: this.sku,
          styleNo: this.styleNo,
          candidates: [
            ...window.AkPriceList.getStyleCandidates(title),
            ...window.AkPriceList.getStyleCandidates(this.sku)
          ],
          priceRow: row,
          tiers: this.wholesaleTiers
        });
        return;
      }

      const tierDefs = [
        { label: '1-71 pcs', key: 1 },
        { label: '72-299 pcs', key: 72 },
        { label: '300+ pcs', key: 300 }
      ];

      this.wholesaleTiers = tierDefs
        .filter((tier) => row.tiers[tier.key] != null)
        .map((tier) => ({
          label: tier.label,
          price: window.AkPriceList.formatMoney(row.tiers[tier.key])
        }));

      window.debugLog('[AkPriceList] product lookup', {
        title: this.$el.dataset.productTitle || '',
        sku: this.sku,
        styleNo: this.styleNo,
        priceRow: row,
        tiers: this.wholesaleTiers
      });
    },

    get visibleMedia() {
      return this.media.filter((m) => {
        if (!m.groupName) return true;
        if (!this.selectedColorValue) return true;
        return m.groupValue === this.selectedColorValue;
      });
    },

    get mainImage() {
      const visible = this.visibleMedia;
      const selected = visible.find((m) => m.id === this.selectedMediaId);
      return selected || visible[0] || { src: '', alt: '', aspectRatio: 1 };
    },

    updateMedia(variant) {
      if (this.colorOptionPosition) {
        this.selectedColorValue = (variant['option' + this.colorOptionPosition] || '').toLowerCase();
      }

      const featuredId = variant.featured_media ? variant.featured_media.id : null;
      const visible = this.visibleMedia;

      if (featuredId && visible.some((m) => m.id === featuredId)) {
        this.selectedMediaId = featuredId;
      } else if (visible.length > 0) {
        this.selectedMediaId = visible[0].id;
      }
    },

    syncQuantities() {
      if (this.hasOnlyDefaultVariant) {
        if (this.quantities.default === undefined) this.quantities.default = '';
        return;
      }

      this.sizeOptions.forEach((size) => {
        if (this.quantities[size] === undefined) this.quantities[size] = '';
      });
    },

    formatOrderQuantities() {
      if (this.hasOnlyDefaultVariant) return '';

      const ordered = this.sizeOptions.length
        ? this.sizeOptions
        : Object.keys(this.quantities).filter((key) => key !== 'default');
      const extras = Object.keys(this.quantities).filter(
        (key) => key !== 'default' && !ordered.includes(key)
      );

      return [...ordered, ...extras]
        .map((size) => {
          const quantity = parseInt(this.quantities[size], 10);
          if (!quantity || Number.isNaN(quantity)) return null;
          return `${size}: ${quantity}`;
        })
        .filter(Boolean)
        .join(', ');
    },

    syncOrderQuantitiesField() {
      const field = this.$refs.orderQuantitiesHidden
        || this.$el.querySelector('[name="contact[Order Quantities]"]');
      if (!field) return;
      field.value = this.formatOrderQuantities();
    },

    getTotalQuantity() {
      return Object.values(this.quantities).reduce((total, value) => {
        const quantity = parseInt(value, 10);
        return total + (Number.isNaN(quantity) ? 0 : quantity);
      }, 0);
    },

    getSelectedColor() {
      const label = this.$el.querySelector('.ak-wholesale-order__color-swatch .color-swatch__radio:checked + label .ak-wholesale-order__color-swatch-label');
      if (label) return label.textContent.trim();

      const value = this.$el.querySelector('.ak-wholesale-order__selected-color-value');
      if (value) return value.textContent.trim();

      return this.color;
    },

    updateSelectedColorDisplay(sourceSwatch) {
      const swatch = sourceSwatch || this.$el.querySelector('.ak-wholesale-order__color-swatch .color-swatch__radio:checked')?.closest('.ak-wholesale-order__color-swatch');
      if (!swatch) return;

      const name = getSwatchColorName(swatch);
      if (!name) return;

      const selectedValue = this.$el.querySelector('.ak-wholesale-order__color-option .product-form__selected-value');
      if (selectedValue) selectedValue.textContent = name;

      const metaValue = this.$el.querySelector('.ak-wholesale-order__selected-color-value');
      if (metaValue) metaValue.textContent = name;

      syncWholesaleSwatchDisplay(swatch, this.$el.querySelector('.ak-wholesale-order__selected-color-swatch'));
    },

    syncReturnTo() {
      const returnTo = this.$el.querySelector('[data-wholesale-return-to]');
      if (!returnTo) return;

      const url = new URL(window.location.href);

      if (this.customOrderFields) {
        returnTo.value = `${url.pathname}?wholesale_submitted=1`;
        return;
      }

      url.searchParams.set('wholesale_submitted', '1');
      returnTo.value = `${url.pathname}${url.search}`;
    },

    bindContactFormSubmit() {
      const contactForm = this.$el.querySelector('form.ak-wholesale-order__contact-form');
      if (!contactForm || contactForm.dataset.wholesaleSubmitBound === 'true') return;

      contactForm.dataset.wholesaleSubmitBound = 'true';
      contactForm.addEventListener('ak-wholesale-cart:before-pdf', (event) => {
        this.submitForm(event);
      });
      contactForm.addEventListener('submit', (event) => {
        this.submitForm(event);
      }, true);
    },

    showQtyError(type, message) {
      this.qtyErrorType = type;
      this.qtyErrorMessage = message || '';

      this.$nextTick(() => {
        const errorEl = this.$refs.qtyError;
        if (!errorEl) return;

        errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        errorEl.focus({ preventScroll: true });
      });
    },

    submitForm(event) {
      if (!event?.target || event.target.tagName !== 'FORM') return;

      const total = this.getTotalQuantity();

      if (this.customOrderFields) {
        if (!this.validateCustomOrderFields()) {
          event.preventDefault();
          event.stopPropagation();
          this.showCustomError();
          return;
        }

        if (this.logoUploading || this.designRefUploading) {
          event.preventDefault();
          event.stopPropagation();
          this.customErrorMessage = 'Please wait for file uploads to finish.';
          this.showCustomError();
          return;
        }

        this.populateCustomOrderFields();
      } else {
        this.syncContactHiddenFields();
      }

      if (this.minimumOrderQuantity > 0 && total < this.minimumOrderQuantity) {
        event.preventDefault();
        event.stopPropagation();
        this.showQtyError('moq', this.moqErrorTemplate
          .replace('{minimum}', this.minimumOrderQuantity)
          .replace('{total}', total));
        return;
      }

      if (total === 0) {
        event.preventDefault();
        event.stopPropagation();
        this.showQtyError('zero');
        return;
      }

      this.qtyErrorType = 'none';
      this.qtyErrorMessage = '';
      this.syncOrderQuantitiesField();
      this.syncReturnTo();
    },

    addToWholesaleOrder() {
      const total = this.getTotalQuantity();

      if (this.minimumOrderQuantity > 0 && total < this.minimumOrderQuantity) {
        this.showQtyError('moq', this.moqErrorTemplate
          .replace('{minimum}', this.minimumOrderQuantity)
          .replace('{total}', total));
        return;
      }

      if (total === 0) {
        this.showQtyError('zero');
        return;
      }

      this.qtyErrorType = 'none';
      this.qtyErrorMessage = '';
      this.syncOrderQuantitiesField();

      const handle = this.$el.dataset.productHandle || '';
      const title = this.$el.dataset.productTitle || '';
      const fallbackImage = this.$el.dataset.productFallbackImage || '';
      const variant = window.AkWholesaleImages?.readVariantFromForm(this.productFormId)
        || { id: this.$el.querySelector('[name="id"]')?.value, sku: this.sku };
      const imageUrl = window.AkWholesaleImages?.urlFromVariant(variant, fallbackImage)
        || this.mainImage?.src
        || '';

      const result = window.AkWholesaleCart?.addLine({
        productHandle: handle,
        productTitle: title,
        variantId: String(variant?.id || this.$el.querySelector('[name="id"]')?.value || ''),
        sku: this.sku,
        color: this.color,
        imageUrl: imageUrl,
        quantities: { ...this.quantities }
      });

      if (!result?.ok) {
        this.showQtyError('zero');
        return;
      }

      if (window.AkWholesaleCart?.openDrawer) {
        window.AkWholesaleCart.openDrawer();
      }
    }
  }));

  Alpine.data('AkPriceListTable', () => ({
    loading: true,
    state: 'loading',
    rows: [],
    loadingText: '',
    emptyText: '',
    errorText: '',

    init() {
      this.loadingText = this.$el.dataset.akLoadingText || 'Loading price list…';
      this.emptyText = this.$el.dataset.akEmptyText || 'No price list data is available.';
      this.errorText = this.$el.dataset.akErrorText || 'Unable to load the price list. Please try again later.';

      this.loadRows();

      if (!window.AkPriceList?.isReady()) {
        document.addEventListener('ak-pricelist:ready', () => this.loadRows(), { once: true });
      }
    },

    async loadRows() {
      this.loading = true;
      this.state = 'loading';

      if (!document.getElementById('ak-pricelist-url')) {
        this.rows = [];
        this.state = 'empty';
        this.loading = false;
        return;
      }

      if (!window.AkPriceList) {
        this.rows = [];
        this.state = 'error';
        this.loading = false;
        return;
      }

      await (window.AkPriceList.ready || Promise.resolve());

      if (!window.AkPriceList.isReady()) {
        this.rows = [];
        this.state = 'error';
        this.loading = false;
        return;
      }

      let rows = window.AkPriceList.getAllRows();
      const limit = parseInt(this.$el.dataset.akRowLimit, 10);

      if (limit > 0) rows = rows.slice(0, limit);

      this.rows = rows;
      this.state = rows.length ? 'ready' : 'empty';
      this.loading = false;
    }
  }));

  Alpine.data('WholesaleOrderDrawer', () => ({
    lines: [],
    isOpen: false,

    init() {
      this.refresh();
      document.addEventListener('ak-wholesale-cart:updated', () => this.refresh());
      this.$el.addEventListener('ak-wholesale-cart:open', () => {
        this.isOpen = true;
        this.refresh();
      });
      this.$el.addEventListener('ak-wholesale-cart:close', () => {
        this.isOpen = false;
      });
    },

    hydrateLine(line) {
      return {
        ...line,
        productTitle: (line.productTitle || '').trim(),
        displayImageUrl: window.AkWholesaleImages?.normalizeUrl(line.imageUrl) || ''
      };
    },

    refresh() {
      this.lines = (window.AkWholesaleCart?.getLines() || []).map((line) => this.hydrateLine(line));
      this.updateHeaderBadge();
      this.enrichLines();
    },

    async enrichLines() {
      if (!window.AkWholesaleImages) return;

      const nextLines = [];

      for (const line of this.lines) {
        let productTitle = (line.productTitle || '').trim();
        let displayImageUrl = line.displayImageUrl || '';
        let imageUrl = line.imageUrl || '';
        const patch = {};

        if (line.productHandle) {
          const product = await window.AkWholesaleImages.fetchProductJson(line.productHandle);

          if (product?.title) {
            productTitle = product.title;
            patch.productTitle = product.title;
          }

          if (!displayImageUrl) {
            const url = window.AkWholesaleImages.imageFromProductJson(product, line.variantId);
            if (url) {
              displayImageUrl = url;
              imageUrl = url;
              patch.imageUrl = url;
            }
          }
        }

        if (Object.keys(patch).length) {
          window.AkWholesaleCart?.updateLineFields(line.lineId, patch);
        }

        nextLines.push({
          ...line,
          productTitle,
          imageUrl: imageUrl || line.imageUrl,
          displayImageUrl
        });
      }

      this.lines = nextLines;
    },

    updateHeaderBadge() {
      const count = this.lines.length;
      document.querySelectorAll('[data-ak-wholesale-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    open() {
      window.AkWholesaleCart?.openDrawer();
    },

    close() {
      window.AkWholesaleCart?.closeDrawer();
    },

    setPanelHeight() {
      window.AkWholesaleCart?.setPanelHeight(this.$el);
    },

    remove(lineId) {
      window.AkWholesaleCart?.removeLine(lineId);
      this.refresh();
    }
  }));

  Alpine.data('WholesalePdpPricing', () => ({
    wholesaleTiers: [],
    priceListReady: false,
    sku: '',

    init() {
      this.sku = this.$el.dataset.initialSku || '';

      document.addEventListener('variant:changed', (event) => {
        if (!this.$el.isConnected) return;
        const formId = this.$el.dataset.productFormId;
        if (formId && event.target?.getAttribute('id') !== formId) return;

        const variant = event.detail.variant;
        if (!variant) return;

        this.sku = variant.sku || '';
        this.updateWholesalePrice();
      });

      this.initWholesalePrice();
    },

    async initWholesalePrice() {
      if (!window.AkPriceList) return;

      await (window.AkPriceList.ready || Promise.resolve());
      this.priceListReady = window.AkPriceList.isReady();
      this.updateWholesalePrice();
    },

    updateWholesalePrice() {
      if (!window.AkPriceList || !this.priceListReady) {
        this.wholesaleTiers = [];
        return;
      }

      const title = this.$el.dataset.productTitle || '';
      let row = window.AkPriceList.findPriceForSource(title);
      if (!row?.tiers && this.sku) {
        row = window.AkPriceList.findPriceForSource(this.sku);
      }

      if (!row?.tiers) {
        this.wholesaleTiers = [];
        return;
      }

      const tierDefs = [
        { label: '1-71 pcs', key: 1 },
        { label: '72-299 pcs', key: 72 },
        { label: '300+ pcs', key: 300 }
      ];

      this.wholesaleTiers = tierDefs
        .filter((tier) => row.tiers[tier.key] != null)
        .map((tier) => ({
          label: tier.label,
          price: window.AkPriceList.formatMoney(row.tiers[tier.key])
        }));
    }
  }));

  Alpine.data('WholesalePdpAdd', () => ({
    quantities: {},
    errorMessage: '',
    sku: '',
    color: '',
    variantId: '',
    productFormId: '',
    imageUrl: '',
    productFallbackImage: '',

    init() {
      this.productFormId = this.$el.dataset.productFormId || '';
      this.sku = this.$el.dataset.initialSku || '';
      this.variantId = this.$el.dataset.initialVariantId || '';
      this.productFallbackImage = this.$el.dataset.productFallbackImage || '';

      let sizeOptions = [];
      try {
        sizeOptions = JSON.parse(this.$el.dataset.sizeOptions || '[]');
      } catch (error) {
        sizeOptions = [];
      }

      sizeOptions.forEach((size) => {
        if (this.quantities[size] === undefined) this.quantities[size] = '';
      });

      this.color = this.readColorFromDom();

      const initialVariant = window.AkWholesaleImages?.readVariantFromForm(this.productFormId);
      if (initialVariant) {
        this.applyVariant(initialVariant);
      } else {
        this.imageUrl = window.AkWholesaleImages?.normalizeUrl(this.$el.dataset.initialImageUrl) || this.productFallbackImage;
      }

      document.addEventListener('variant:changed', (event) => {
        if (!this.$el.isConnected) return;
        const formId = event.detail?.formId || event.target?.getAttribute?.('id');
        if (this.productFormId && formId && formId !== this.productFormId) return;
        const variant = event.detail?.variant;
        if (!variant) return;
        this.applyVariant(variant);
      });
    },

    sanitizeNumeric(event) {
      const input = event.target;
      const sanitized = (input.value || '').replace(/\D/g, '');
      if (input.value !== sanitized) input.value = sanitized;
    },

    readColorFromDom() {
      const form = this.productFormId ? document.getElementById(this.productFormId) : null;
      const root = form?.closest('.product-form') || this.$el.closest('.product-info')?.querySelector('.product-form');
      if (!root) return '';

      const optionNames = root.querySelectorAll('.product-form__option-name');
      for (const optionName of optionNames) {
        const labelText = (optionName.textContent || '').split(':')[0].trim().toLowerCase();
        if (labelText !== 'color' && labelText !== 'colour') continue;
        const valueEl = optionName.querySelector('.product-form__selected-value');
        return valueEl ? valueEl.textContent.trim() : '';
      }

      return '';
    },

    productTitleFromDom() {
      const fromData = (this.$el.getAttribute('data-product-title') || '').trim();
      if (fromData) return fromData;
      const heading = document.querySelector('.product-meta__title');
      return heading ? heading.textContent.trim() : '';
    },

    applyVariant(variant) {
      this.variantId = variant.id;
      this.sku = variant.sku || '';
      this.color = this.readColorFromDom();
      this.imageUrl = window.AkWholesaleImages?.urlFromVariant(variant, this.productFallbackImage) || '';
    },

    getTotalQuantity() {
      return Object.values(this.quantities).reduce((total, value) => {
        const quantity = parseInt(value, 10);
        return total + (Number.isNaN(quantity) ? 0 : quantity);
      }, 0);
    },

    addToOrder() {
      this.errorMessage = '';
      const total = this.getTotalQuantity();

      if (total === 0) {
        this.errorMessage = 'Please enter at least one quantity greater than 0.';
        return;
      }

      const variant = window.AkWholesaleImages?.readVariantFromForm(this.productFormId) || { id: this.variantId, sku: this.sku };
      const imageUrl = window.AkWholesaleImages?.urlFromVariant(variant, this.productFallbackImage) || this.imageUrl;
      const productTitle = this.productTitleFromDom();

      const result = window.AkWholesaleCart?.addLine({
        productHandle: this.$el.dataset.productHandle || '',
        productTitle: productTitle,
        variantId: String(variant.id || this.variantId || ''),
        sku: variant.sku || this.sku,
        color: this.color,
        imageUrl: imageUrl,
        quantities: { ...this.quantities }
      });

      if (!result?.ok) {
        this.errorMessage = 'Please enter at least one quantity greater than 0.';
        return;
      }

      Object.keys(this.quantities).forEach((key) => {
        this.quantities[key] = '';
      });

      if (window.AkWholesaleCart?.openDrawer) {
        window.AkWholesaleCart.openDrawer();
      }
    }
  }));

  Alpine.data('WholesaleCartCheckout', () => ({
    lines: [],
    sizeOptions: [],
    qtyErrorMessage: '',
    minimumOrderQuantity: 0,
    moqErrorTemplate: '',
    showFormSuccess: false,

    init() {
      this.minimumOrderQuantity = parseInt(this.$el.dataset.minimumOrderQuantity, 10) || 0;
      this.moqErrorTemplate = this.$el.dataset.moqErrorMessage || '';
      try {
        this.sizeOptions = JSON.parse(this.$el.dataset.sizeOptions || '[]');
      } catch (error) {
        this.sizeOptions = [];
      }
      if (!Array.isArray(this.sizeOptions)) this.sizeOptions = [];
      this.refresh();
      document.addEventListener('ak-wholesale-cart:updated', () => this.refresh());
      this.bindSubmit();
      this.checkSuccess();
    },

    hydrateLine(line) {
      return {
        ...line,
        productTitle: (line.productTitle || '').trim(),
        displayImageUrl: window.AkWholesaleImages?.normalizeUrl(line.imageUrl) || ''
      };
    },

    async enrichLines() {
      if (!window.AkWholesaleImages) return;

      const nextLines = [];

      for (const line of this.lines) {
        let productTitle = (line.productTitle || '').trim();
        let displayImageUrl = line.displayImageUrl || '';
        let imageUrl = line.imageUrl || '';
        const patch = {};

        if (line.productHandle) {
          const product = await window.AkWholesaleImages.fetchProductJson(line.productHandle);

          if (product?.title) {
            productTitle = product.title;
            patch.productTitle = product.title;
          }

          if (!displayImageUrl) {
            const url = window.AkWholesaleImages.imageFromProductJson(product, line.variantId);
            if (url) {
              displayImageUrl = url;
              imageUrl = url;
              patch.imageUrl = url;
            }
          }
        }

        if (Object.keys(patch).length) {
          window.AkWholesaleCart?.updateLineFields(line.lineId, patch);
        }

        nextLines.push({
          ...line,
          productTitle,
          imageUrl: imageUrl || line.imageUrl,
          displayImageUrl
        });
      }

      this.lines = nextLines;
    },

    async refresh() {
      if (window.AkWholesaleCart?.resolveLineTitles) {
        await window.AkWholesaleCart.resolveLineTitles();
      }
      this.lines = (window.AkWholesaleCart?.getLines() || []).map((line) => this.hydrateLine(line));
      this.syncOrderLinesField();
      await this.enrichLines();
    },

    getTotalUnits() {
      return window.AkWholesaleCart?.getTotalUnits(this.lines) || 0;
    },

    syncOrderLinesField() {
      const field = this.$refs.orderLinesHidden;
      if (!field || !window.AkWholesaleCart) return;
      field.value = window.AkWholesaleCart.formatOrderLinesForContact(this.lines);
    },

    remove(lineId) {
      window.AkWholesaleCart?.removeLine(lineId);
      this.refresh();
    },

    setLineQuantity(lineId, size, rawValue) {
      if (!window.AkWholesaleCart) return;

      const line = this.lines.find((item) => item.lineId === lineId);
      if (!line) return;

      const quantities = { ...(line.quantities || {}) };
      quantities[size] = rawValue;
      window.AkWholesaleCart.updateLineQuantities(lineId, quantities);
      this.lines = (window.AkWholesaleCart.getLines() || []).map((item) => this.hydrateLine(item));
      this.syncOrderLinesField();
      this.qtyErrorMessage = '';
    },

    sanitizeLineQuantity(event, lineId, size) {
      const input = event.target;
      const sanitized = (input.value || '').replace(/\D/g, '');
      if (input.value !== sanitized) input.value = sanitized;
      this.setLineQuantity(lineId, size, sanitized);
    },

    prepareCartCheckout(form, event) {
      this.qtyErrorMessage = '';

      if (!this.lines.length) {
        if (event) event.preventDefault();
        this.qtyErrorMessage = 'Your order is empty. Add products before submitting.';
        return false;
      }

      const total = this.getTotalUnits();
      if (this.minimumOrderQuantity > 0 && total < this.minimumOrderQuantity) {
        if (event) event.preventDefault();
        this.qtyErrorMessage = this.moqErrorTemplate
          .replace('{minimum}', this.minimumOrderQuantity)
          .replace('{total}', total);
        return false;
      }

      this.syncOrderLinesField();
      const returnTo = form.querySelector('[data-wholesale-return-to]');
      if (returnTo) {
        const url = new URL(window.location.href);
        url.searchParams.set('wholesale_submitted', '1');
        returnTo.value = `${url.pathname}${url.search}`;
      }

      return true;
    },

    bindSubmit() {
      const form = this.$el.querySelector('form.ak-wholesale-cart-checkout-form');
      if (!form || form.dataset.akCheckoutBound === 'true') return;

      form.dataset.akCheckoutBound = 'true';

      form.addEventListener('ak-wholesale-cart:before-pdf', (event) => {
        this.prepareCartCheckout(form, event);
      });

      form.addEventListener('submit', (event) => {
        if (!this.prepareCartCheckout(form, event)) return;
      }, true);
    },

    checkSuccess() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('wholesale_submitted') !== '1' && params.get('contact_posted') !== '1') return;

      this.showFormSuccess = true;
      this.lines = [];
      this.qtyErrorMessage = '';

      const section = this.$el.closest('section.ak-wholesale-order');
      if (section) {
        section.setAttribute('data-ak-view', 'checkout');
        section.setAttribute('data-ak-order-submitted', '1');
      }

      window.AkWholesaleCart?.clear();
      window.AkWholesaleCart?.closeDrawer?.();

      const url = new URL(window.location.href);
      url.searchParams.delete('wholesale_submitted');
      url.searchParams.delete('contact_posted');
      const search = url.searchParams.toString();
      window.history.replaceState({}, '', (search ? `${url.pathname}?${search}` : url.pathname) + url.hash);

      this.$nextTick(() => {
        const successEl = this.$refs.formSuccess;
        if (successEl) successEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }));
});

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-action="close-ak-wholesale-drawer"]')) {
    event.preventDefault();
    window.AkWholesaleCart?.closeDrawer();
    return;
  }

  const toggle = event.target.closest('[data-action="toggle-ak-wholesale-cart"]');
  if (toggle) {
    event.preventDefault();
    const drawer = document.getElementById('ak-wholesale-order-drawer');
    const isOpen = drawer?.getAttribute('aria-hidden') === 'false';

    if (isOpen) {
      window.AkWholesaleCart?.closeDrawer();
    } else {
      window.AkWholesaleCart?.openDrawer();
    }
    return;
  }

  const drawer = document.getElementById('ak-wholesale-order-drawer');
  if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;
  if (event.target.closest('#ak-wholesale-order-drawer .drawer__inner')) return;

  window.AkWholesaleCart?.closeDrawer();
}, true);

window.addEventListener('resize', () => {
  const drawer = document.getElementById('ak-wholesale-order-drawer');
  if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;
  const panel = drawer.querySelector('.collection-drawer');
  if (panel) panel.style.maxHeight = `${window.innerHeight}px`;
});

function getSwatchColorName(swatchEl) {
  const customLabel = swatchEl.querySelector('.ak-wholesale-order__color-swatch-label');
  if (customLabel) return customLabel.textContent.trim();

  return swatchEl.querySelector('label.color-swatch__item')?.getAttribute('title')?.trim() || '';
}

function syncWholesaleSwatchDisplay(sourceSwatchEl, targetSwatchEl) {
  const sourceItem = sourceSwatchEl?.querySelector('label.color-swatch__item');
  if (!sourceItem || !targetSwatchEl) return;

  const preserveClasses = ['color-swatch__item', 'ak-wholesale-order__selected-color-swatch'];
  targetSwatchEl.className = preserveClasses.filter((className) => targetSwatchEl.classList.contains(className)).join(' ');
  targetSwatchEl.setAttribute('style', sourceItem.getAttribute('style') || '');

  [...sourceItem.classList].forEach((className) => {
    if (className.startsWith('color-swatch--')) targetSwatchEl.classList.add(className);
  });

  targetSwatchEl.classList.toggle('color-swatch--white', sourceSwatchEl.classList.contains('color-swatch--white'));
}

function syncWholesaleColorState(swatchEl) {
  const layout = swatchEl.closest('.ak-wholesale-order__layout');
  if (!layout?._x_dataStack?.[0]?.updateSelectedColorDisplay) return;

  layout._x_dataStack[0].updateSelectedColorDisplay(swatchEl);
}

function getSelectedValueEl(swatchEl) {
  return swatchEl.closest('.product-form__option')?.querySelector('.product-form__selected-value');
}

document.addEventListener('mouseover', (event) => {
  const swatch = event.target.closest('.ak-wholesale-order__color-swatches .color-swatch');
  if (!swatch) return;

  const selectedValueEl = getSelectedValueEl(swatch);
  if (!selectedValueEl) return;

  const name = getSwatchColorName(swatch);
  if (name) selectedValueEl.textContent = name;
});

document.addEventListener('mouseout', (event) => {
  const swatchList = event.target.closest('.ak-wholesale-order__color-swatches');
  if (!swatchList) return;

  if (event.relatedTarget && swatchList.contains(event.relatedTarget)) return;

  const checked = swatchList.querySelector('.color-swatch__radio:checked')?.closest('.color-swatch');
  if (checked) syncWholesaleColorState(checked);
});

document.addEventListener('change', (event) => {
  if (!event.target.matches('.ak-wholesale-order__color-swatches .color-swatch__radio')) return;

  const swatch = event.target.closest('.color-swatch');
  if (swatch) syncWholesaleColorState(swatch);
});

